import { Component, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBodyLayout } from '../hooks/use-body-layout';

/**
 * The app had no boundary at all until FRA-20260908-001, and the failure it
 * leaves behind is total: `index.html` mounts React into `<body id="root">`, so
 * an unhandled render throw unmounts the tree and leaves a genuinely blank
 * page — no container, no chrome, no way back except retyping the URL.
 *
 * Two failure classes reach here, and they need different words:
 *
 *  - A RENDER THROW in a page. Recoverable by navigating elsewhere, so the
 *    boundary resets on route change and the copy points at the rest of the app.
 *  - A CHUNK LOAD FAILURE. Every page is `lazy()`, so a deploy that replaces
 *    hashed bundles under a client holding a stale HTML shell makes the next
 *    navigation reject. Navigating again cannot fix it — the shell is wrong —
 *    so that case has to ask for a reload instead.
 */

// Vite's dynamic-import failures are not one string across browsers, and the
// user-visible advice is wrong if we guess. Match the shapes Vite and the
// browsers actually produce rather than a single substring.
function isChunkLoadError(error: Error | null): boolean {
    if (!error) return false;
    const text = `${error.name} ${error.message}`;
    return (
        /ChunkLoadError/i.test(text) ||
        /Loading chunk [\w-]+ failed/i.test(text) ||
        /Failed to fetch dynamically imported module/i.test(text) ||
        /error loading dynamically imported module/i.test(text) ||
        /Importing a module script failed/i.test(text)
    );
}

interface Props {
    children: ReactNode;
    /** Changing this clears a caught error — the route path, in practice. */
    resetKey: string;
    fallback: (error: Error, isChunkError: boolean) => ReactNode;
}

interface State {
    error: Error | null;
}

class ErrorBoundaryInner extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidUpdate(prev: Props) {
        // Navigating away is the user's escape hatch from a page that threw.
        // Without this the fallback would survive the route change and the app
        // would look permanently broken.
        if (prev.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({ error: null });
        }
    }

    componentDidCatch(error: Error, info: { componentStack?: string | null }) {
        // No error reporting service is wired up in this app yet; the console
        // is what a developer reproducing a user's report actually has.
        console.error('Unhandled render error:', error, info.componentStack);
    }

    render() {
        if (this.state.error) {
            return this.props.fallback(this.state.error, isChunkLoadError(this.state.error));
        }
        return this.props.children;
    }
}

function ErrorScreen({ error, isChunkError }: { error: Error; isChunkError: boolean }) {
    const { t } = useTranslation();

    // Same escape as the other message screens: the body is laid out as a
    // sidebar grid, and a screen with no sidebar collapses into the 160px
    // column without this.
    useBodyLayout('plain');

    if (isChunkError) {
        return (
            <div className="message_screen">
                <div className="message_screen_card">
                    <em className="icon-refresh message_screen_icon" aria-hidden="true"></em>
                    <h1>{t('error.boundary.stale.title', 'A new version is available')}</h1>
                    <p>
                        {t(
                            'error.boundary.stale.message',
                            'This page was updated while you had it open. Reload to get the latest version.',
                        )}
                    </p>
                    <div className="message_screen_actions">
                        <button
                            type="button"
                            className="z2 primary"
                            onClick={() => window.location.reload()}
                        >
                            {t('error.boundary.reload', 'Reload')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="message_screen">
            <div className="message_screen_card">
                <em className="icon-stop message_screen_icon" aria-hidden="true"></em>
                <h1>{t('error.boundary.title', 'Something went wrong')}</h1>
                <p>
                    {t(
                        'error.boundary.message',
                        'This page could not be displayed. The rest of the app still works.',
                    )}
                </p>
                {/* The message is for a colleague reading a screenshot, not for
                    the user to act on — hence a details element rather than the
                    stack on the page. */}
                {import.meta.env.DEV && (
                    <details style={{ marginTop: '1rem', textAlign: 'left' }}>
                        <summary>{t('error.boundary.details', 'Technical details')}</summary>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                            {error.stack || error.message}
                        </pre>
                    </details>
                )}
                <div className="message_screen_actions">
                    <Link to="/" role="button" className="z2 primary">
                        {t('error.boundary.back_to_home', 'Back to homepage')}
                    </Link>
                    <button
                        type="button"
                        className="z2"
                        onClick={() => window.location.reload()}
                    >
                        {t('error.boundary.reload', 'Reload')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Route-level boundary. Resets whenever the path changes, so one broken page
 * never makes the whole app look dead.
 */
export default function ErrorBoundary({ children }: { children: ReactNode }) {
    const location = useLocation();

    return (
        <ErrorBoundaryInner
            resetKey={location.pathname}
            fallback={(error, isChunkError) => (
                <ErrorScreen error={error} isChunkError={isChunkError} />
            )}
        >
            {children}
        </ErrorBoundaryInner>
    );
}
